import { useMemo, useState } from 'react';

import { useQuery as useApolloQuery, useMutation } from '@apollo/client';
import { useQuery } from '@tanstack/react-query';
import { Button } from 'primereact/button';

import { breedsOptions } from '@/services/lookups';
import {
  ADD_CALVES,
  Calf,
  Equipment,
  GET_PENS,
  NewCalfEntryInput,
  Owner,
} from '@/types.gql';

import { CalfEntryForm } from './CalfEntryForm';
import { CalfEntryLogged } from './CalfEntryLogged';
import { CalfEntryRequirements } from './CalfEntryRequirements';
import { initialRequirementsState } from './state';
import { BreedPartial, NewCalfEntryInputExtended, PenPartial } from './types';
import CalfEntryContext from './useCalfEntryContext';

import './CalfEntry.css';

type CalfEntryProps = {
  className?: string;
};

export const CalfEntryPage = ({ className }: CalfEntryProps) => {
  const [receivedDate, setReceivedDate] = useState<Date | null>(
    initialRequirementsState.receivedDate,
  );
  const [owner, setOwner] = useState<Owner | null>(
    initialRequirementsState.owner,
  );
  const [herdsman, setHerdsman] = useState<Equipment | null>(
    initialRequirementsState.herdsman,
  );
  const [calves, setCalves] = useState<NewCalfEntryInput[]>(
    [] as NewCalfEntryInput[],
  );

  const { data: penData } = useApolloQuery(GET_PENS);
  const { data: breedData } = useQuery(breedsOptions());

  const [addCalves] = useMutation(ADD_CALVES);

  const value = useMemo(
    () => ({
      receivedDate,
      setReceivedDate,
      owner,
      setOwner,
      herdsman,
      setHerdsman,
      calves,
      setCalves,
    }),
    [calves, herdsman, owner, receivedDate],
  );

  const isSubmittable = useMemo(() => {
    return (
      value.calves.length > 0 &&
      value.receivedDate &&
      value.owner &&
      value.herdsman
    );
  }, [value.calves, value.herdsman, value.owner, value.receivedDate]);

  const mapCalvesToNewCalfEntryInput = (calves: NewCalfEntryInput[]) => {
    return calves.map((calf: NewCalfEntryInput) => {
      const tempCalf = { ...calf } as NewCalfEntryInputExtended;

      const penId = tempCalf.penId;
      const penName = penData?.pens?.items?.find(
        (p) => p.id === tempCalf.penId,
      )?.name;
      const yardId = penData?.pens?.items?.find((p) => p.id === tempCalf.penId)
        ?.yard?.id;
      const breedId = tempCalf.breedId;
      const breedName = breedData?.find((b) => b.id === tempCalf.breedId)?.name;

      delete tempCalf.breed;
      delete tempCalf.pen;

      return {
        ...tempCalf,
        penId,
        penName,
        yardId,
        breedId,
        breedName,
      };
    });
  };

  const handleAddCalves = () => {
    if (!receivedDate || !owner || !herdsman) return;

    const mappedCalves = mapCalvesToNewCalfEntryInput(calves);
    const mappedCalvesForSubmission = mappedCalves.map((calf) => {
      const tempCalf = { ...calf } as NewCalfEntryInputExtended;

      delete tempCalf.penName;
      delete tempCalf.breedName;

      return tempCalf;
    });

    addCalves({
      variables: {
        requirements: {
          receivedDate: receivedDate.toString(),
          ownerId: owner.id,
          herdsmanId: herdsman.id,
        },
        calves: mappedCalvesForSubmission,
      },
    }).then(() => {
      setCalves([]);
    });
  };

  const handleCalfEntryDeleteAction = (calf: Calf) => {
    if (!calves) return;
    setCalves(calves.filter((c) => c.registry !== calf.registry)); // TODO: fix where all calves are being deleted
  };

  return (
    <div className={`calf-entry common-page ${className}`.trim()}>
      <CalfEntryContext.Provider value={value}>
        <div className="calf-entry-header">
          <CalfEntryRequirements />
          <CalfEntryLogged
            handleCalfEntryDeleteAction={handleCalfEntryDeleteAction}
          />
          <Button
            className="calf-entry__save"
            label="Save"
            disabled={!isSubmittable}
            onClick={handleAddCalves}
          />
        </div>
        <CalfEntryForm
          pens={penData?.pens?.items as PenPartial[]}
          breeds={breedData as BreedPartial[]}
        />
      </CalfEntryContext.Provider>
    </div>
  );
};
